import { useEffect, useState } from 'react';
import { Icon } from '../../design-system/icons';
import { type InstructionExamples } from './accountSetup';

/**
 * Overlay de instrucciones para subir una buena foto de producto (paso 4 del
 * cuestionario, Figma node 172:324). Se muestra sobre la pantalla al tocar el
 * recuadro "Subir foto". Fondo oscuro con blur; ejemplos de fotos buenas (check
 * verde) y a evitar (cruz roja); un botón "Subir foto" que dispara el file picker.
 *
 * Entra con un fade + slide-up sutil para que no aparezca de golpe (ADR-717).
 */

function InstructionImage({ src, kind }: { src: string; kind: 'good' | 'bad' }) {
  const good = kind === 'good';
  return (
    <div className="relative h-[173px] flex-1 overflow-hidden rounded-[16px]">
      <img src={src} alt="" draggable={false} className="size-full object-cover" />
      <span
        className={`absolute bottom-2.5 right-2.5 grid size-6 place-items-center rounded-full border border-[#F4F4F5] ${
          good ? 'bg-[#10B981]' : 'bg-[#EF4444]'
        }`}
      >
        <Icon name={good ? 'outline/check' : 'outline/cross'} size={14} className="text-white" />
      </span>
    </div>
  );
}

export interface UploadInstructionsOverlayProps {
  /** Cierra el overlay sin subir nada. */
  onClose: () => void;
  /** Abre el selector de archivos del SO para elegir la foto. */
  onPickFile: () => void;
  /**
   * Ejemplos positivos del rubro elegido en el paso 2 (producto solo + en uso). Los
   * pasa el paso 4, que ya tiene las categorías. Sin esto, un usuario de gastronomía
   * veía ropa como ejemplo de cómo fotografiar su producto.
   */
  examples: InstructionExamples;
}

export function UploadInstructionsOverlay({
  onClose,
  onPickFile,
  examples,
}: UploadInstructionsOverlayProps) {
  const [shown, setShown] = useState(false);

  // Entrada animada (fade del fondo + slide-up del contenido) y cierre con Escape.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Instrucciones para subir una foto"
      onClick={onClose}
      className={`fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-5 backdrop-blur-[5px] transition-opacity duration-300 ease-out md:items-center ${
        shown ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`flex h-full w-full max-w-[402px] flex-col gap-8 pb-5 pt-[50px] transition-transform duration-300 ease-out md:h-auto md:max-h-[calc(100vh-40px)] md:max-w-[800px] md:p-8 ${
          shown ? 'translate-y-0' : 'translate-y-4'
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="-m-1 shrink-0 self-start rounded-full p-1 text-[#FAFAFA] transition-colors hover:bg-white/10"
        >
          <Icon name="outline/cross" size={24} />
        </button>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto md:gap-6">
          <h2 className="text-[26px] font-[600] leading-[31px] text-[#FAFAFA]">
            Instrucciones para cargar una buena foto de producto
          </h2>

          {/* Mobile: bloques apilados (buenos arriba, a evitar abajo). Desktop (`md:`): en
              fila → los ejemplos "a evitar" quedan a la DERECHA de los "buenos", para aprovechar
              el ancho. Cada columna conserva su texto + su par de fotos. */}
          <div className="flex flex-col gap-8 md:flex-row md:gap-12">
            <div className="flex flex-1 flex-col gap-4">
              <p className="text-[14px] font-[400] leading-[19px] text-[#FAFAFA]">
                Subí una foto clara y bien iluminada donde el producto se vea completo, siendo utilizado
                en contexto. Con un fondo simple y neutro funciona mejor.
                {examples.tip ? ` ${examples.tip}` : ''}
              </p>
              <div className="flex gap-4">
                {/* Los dos ejemplos del rubro: el producto solo y el producto en uso. */}
                <InstructionImage src={examples.solo} kind="good" />
                <InstructionImage src={examples.enUso} kind="good" />
              </div>
            </div>

            <div className="flex flex-1 flex-col gap-4">
              <p className="text-[14px] font-[400] leading-[19px] text-[#FAFAFA]">
                Evitá las fotos borrosas, oscuras o con el producto cortado o tapado. Tampoco uses fondos
                cargados que compitan con el producto.
              </p>
              {/* Las fotos de "qué evitar" son de indumentaria, así que sólo se muestran
                  en moda. En el resto de los rubros queda el texto solo, en vez de
                  ilustrar con ropa a alguien que vende otra cosa. */}
              {examples.malas?.length ? (
                <div className="flex gap-4">
                  {examples.malas.map((src) => (
                    <InstructionImage key={src} src={src} kind="bad" />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onPickFile}
          className="flex w-full shrink-0 items-center justify-center gap-2 rounded-[12px] bg-white p-4 text-[16px] font-[700] leading-[19px] text-[#18181B] transition-colors hover:bg-[#F4F4F5]"
        >
          <Icon name="outline/image-plus" size={20} />
          Subir foto
        </button>
      </div>
    </div>
  );
}

export default UploadInstructionsOverlay;
